import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ToastContainer, toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import API_PATHS from '../../apiCalls/ApiPath';
import { getAllAddressCreator, loginActions, resetCreateKidSuccessCreator } from '../../slices/authSlice';
import TopGreenHeader from '../Common/TopGreenHeader';
import SelectKidsModal from './SelectKidsModal';
import CheckIcon from '../../assets/tickmark.png';
import PlusIcon from '../../assets/plusIcon.png';

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_MON_FRI = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const SelectDaysScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const planChoosed = useSelector(state => state.auth.planChoosed);
  const serviceScheduled = useSelector(state => state.auth.serviceScheduled);
  const getAllPlans = useSelector(state => state.auth.getAllPlans);
  const serviceName = serviceScheduled?.serviceName;

  const isSocialHub = planChoosed?.planService === 'SOCIALHUB';
  // Transport has no per-day kid mapping — pick days, then kids once for all days.
  const isTransport =
    planChoosed?.planService === 'TRANSPORT' || serviceName === 'TRANSPORT';

  const { choseedPlan, numOfKids, isFixedWeekdays } = planChoosed;
  // Plans now carry their own day/kid limits — prefer those, fall back to the
  // legacy "1-2 Days|price" string only if they're missing.
  const planPrice = planChoosed.price ?? (choseedPlan ? choseedPlan.split('|')[1] : 0);
  const minDays = isSocialHub ? 1 : (planChoosed.minDays ?? 1);
  const maxDays = isSocialHub ? 7 : (planChoosed.maxDays ?? minDays);
  const maxKidsPerDay = isSocialHub ? 3 : (numOfKids || 1);

  // For Social Hub: extract the SOCIALHUB plans keyed by noOfKid for quick lookup.
  const socialHubPlans = isSocialHub
    ? (getAllPlans?.Data || []).filter(p => p.service === 'SOCIALHUB')
    : [];
  const socialHubPlanByKidCount = {};
  socialHubPlans.forEach(plan => {
    socialHubPlanByKidCount[plan.noOfKid] = plan;
  });

  // Returns the matched Social Hub plan for a given day based on kid count.
  const getSocialHubPlanForDay = (day) => {
    const dayKids = kidsByDay[day] || [];
    if (dayKids.length === 0) return null;
    return socialHubPlanByKidCount[dayKids.length] || null;
  };

  const [allKidsData, setAllKidsData] = useState([]);
  // { Monday: [kid, ...], ... } — a day is "selected" when it has at least one kid
  const [kidsByDay, setKidsByDay] = useState({});
  const [modalDay, setModalDay] = useState(null);
  // Fixed Mon-Fri plans (Breakfast Club) pick kids once, no day choosing.
  const [fixedKids, setFixedKids] = useState([]);
  const [fixedModalOpen, setFixedModalOpen] = useState(false);
  // Transport: choose any days, then a single set of kids for all of them.
  const [transportDays, setTransportDays] = useState([]);
  const [transportKids, setTransportKids] = useState([]);
  const [transportModalOpen, setTransportModalOpen] = useState(false);

  const fetchAllKids = async () => {
    try {
      const token = await AsyncStorage.getItem('token');
      const clientToken = await AsyncStorage.getItem('clientToken');
      const response = await axios.get(`${API_PATHS.BASE_URL}${API_PATHS.GET_ALL_KIDS_API}`, {
        headers: {
          Authorization: `Bearer ${clientToken || token}`,
        },
      });
      setAllKidsData(response.data.Data.kids);
    } catch (error) {
      toast.error('Unable to fetch kids. Please try again later!');
    }
  };

  useFocusEffect(
    useCallback(() => {
      fetchAllKids();
    }, [])
  );

  // Social Hub now runs the full week too (Sunday - Saturday, up to 7 days).
  const displayedDays = daysOfWeek;
  const selectedDays = displayedDays.filter(day => (kidsByDay[day] || []).length > 0);

  const openDayModal = day => {
    const isDaySelected = (kidsByDay[day] || []).length > 0;
    // Social Hub has no day count limit — users can pick any Mon-Fri.
    if (!isSocialHub && !isDaySelected && selectedDays.length >= maxDays) {
      toast.info(`You can select upto ${maxDays} and minimum ${minDays} days.`);
      return;
    }
    setModalDay(day);
  };

  const handleModalConfirm = kids => {
    setKidsByDay({ ...kidsByDay, [modalDay]: kids });
    setModalDay(null);
  };

  const removeKidFromDay = (day, kidId) => {
    setKidsByDay({
      ...kidsByDay,
      [day]: (kidsByDay[day] || []).filter(kid => kid.id !== kidId),
    });
  };

  const handleAddKid = () => {
    setModalDay(null);
    setFixedModalOpen(false);
    setTransportModalOpen(false);
    dispatch(resetCreateKidSuccessCreator());
    navigation.navigate('AddNewKidScreen', { returnTo: 'SelectDaysScreen' });
  };

  const toggleTransportDay = day => {
    if (transportDays.includes(day)) {
      setTransportDays(transportDays.filter(d => d !== day));
    } else if (transportDays.length >= maxDays) {
      toast.info(`You can select upto ${maxDays} and minimum ${minDays} days.`);
    } else {
      setTransportDays([...transportDays, day]);
    }
  };

  const handleTransportConfirm = () => {
    if (transportDays.length < minDays || transportDays.length > maxDays) {
      toast.info(`You can select upto ${maxDays} and minimum ${minDays} day(s).`);
      return;
    }
    if (transportKids.length === 0) {
      toast.info('Please select at least one kid');
      return;
    }
    // Keep days in calendar order for a tidy payload/preview.
    const orderedDays = daysOfWeek.filter(d => transportDays.includes(d));
    dispatch(loginActions.setDaysSelected(orderedDays));
    dispatch(loginActions.setTotalSelectedKids(transportKids));
    // Transport has no per-day mapping; clear any stale map so ConfirmBooking
    // shows the flat days + kids preview.
    dispatch(loginActions.setKidsByDay({}));
    routeNext();
  };

  const routeNext = () => {
    dispatch(getAllAddressCreator());
    if (serviceName === 'TRANSPORT') {
      navigation.navigate('SelectTransport');
    } else if (serviceName === 'HOMECARE') {
      navigation.navigate('SelectAddressScreen');
    } else if (serviceName === null) {
      navigation.navigate('ProfileScreen');
    } else {
      navigation.navigate('ConfirmBooking');
    }
  };

  const handleFixedConfirm = () => {
    if (fixedKids.length === 0) {
      toast.info('Please select at least one kid');
      return;
    }
    dispatch(loginActions.setDaysSelected(WEEKDAYS_MON_FRI));
    dispatch(loginActions.setTotalSelectedKids(fixedKids));
    routeNext();
  };

  const handleConfirmSelection = () => {
    if (selectedDays.length === 0) {
      toast.info('Please select days and kids for each day');
      return;
    }

    if (!isSocialHub) {
      if (selectedDays.length < minDays || selectedDays.length > maxDays) {
        toast.info(`You can select upto ${maxDays} and minimum ${minDays} days.`);
        return;
      }
    }

    const kidsSelected = [];
    selectedDays.forEach(day => {
      kidsByDay[day].forEach(kid => {
        if (!kidsSelected.some(selected => selected.id === kid.id)) {
          kidsSelected.push(kid);
        }
      });
    });

    dispatch(loginActions.setDaysSelected(selectedDays));
    dispatch(loginActions.setTotalSelectedKids(kidsSelected));

    // Persist per-day kid mapping so ConfirmBooking can build the Social Hub payload.
    const selectedKidsByDay = {};
    selectedDays.forEach(day => {
      selectedKidsByDay[day] = kidsByDay[day];
    });
    dispatch(loginActions.setKidsByDay(selectedKidsByDay));

    // For Social Hub, also store the per-day planId mapping in planChoosed.
    if (isSocialHub) {
      const dayPlanMap = {};
      selectedDays.forEach(day => {
        const matched = getSocialHubPlanForDay(day);
        if (matched) {
          dayPlanMap[day] = matched.planId;
        }
      });
      dispatch(loginActions.setplanChoosed({
        ...planChoosed,
        socialHubDayPlans: dayPlanMap,
      }));
    }

    routeNext();
  };

  const renderDayCard = day => {
    const dayKids = kidsByDay[day] || [];
    const isDaySelected = dayKids.length > 0;

    // For Social Hub, compute the per-day price from the matched plan.
    let dayPrice = planPrice;
    if (isSocialHub && isDaySelected) {
      const matched = getSocialHubPlanForDay(day);
      dayPrice = matched ? matched.price : '0.00';
    }

    return (
      <TouchableOpacity
        key={day}
        style={[styles.dayCard, isDaySelected && styles.dayCardSelected]}
        activeOpacity={0.7}
        onPress={() => openDayModal(day)}>
        <View style={styles.dayCardHeader}>
          <Text style={styles.dayName}>{day}</Text>
          {isDaySelected && (
            <Text style={styles.dayTotal}>Total: ${Number(dayPrice).toFixed(2)}</Text>
          )}
        </View>

        {isDaySelected && (
          <>
            <Text style={styles.selectKidsLabel}>Kids for {day}</Text>
            {dayKids.map(kid => (
              <TouchableOpacity
                key={kid.id}
                style={styles.kidItem}
                onPress={() => removeKidFromDay(day, kid.id)}>
                <Image
                  source={
                    kid.profilePic
                      ? { uri: kid.profilePic }
                      : require('../../assets/AddKidPic.png')
                  }
                  style={styles.kidAvatar}
                />
                <View style={styles.kidInfo}>
                  <Text style={styles.kidName}>{kid.name}</Text>
                  <Text style={styles.kidDetails}>{kid.age} Years</Text>
                </View>
                <Image source={CheckIcon} style={styles.checkIcon} />
              </TouchableOpacity>
            ))}
            {dayKids.length < maxKidsPerDay && (
              <TouchableOpacity style={styles.addSiblingButton} onPress={() => openDayModal(day)}>
                <Image source={PlusIcon} style={styles.plusIcon} />
                <Text style={styles.addSiblingText}>Add sibling</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </TouchableOpacity>
    );
  };

  // Compute Social Hub grand total across all selected days.
  const socialHubGrandTotal = isSocialHub
    ? selectedDays.reduce((sum, day) => {
        const matched = getSocialHubPlanForDay(day);
        return sum + (matched ? Number(matched.price) : 0);
      }, 0)
    : 0;

  if (isTransport) {
    return (
      <>
        <TopGreenHeader
          Heading={'Select Days & Kids'}
          navigation={navigation}
          isBackIcon={true}
          isNotification={false}
        />
        <View style={styles.container}>
          <ToastContainer />
          <Text style={styles.title}>
            Select up to {maxDays} day(s) (minimum {minDays}). The kids you choose
            apply to all selected days.
          </Text>
          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.sectionLabel}>Days</Text>
            <View style={styles.dayChipsRow}>
              {daysOfWeek.map(day => {
                const selected = transportDays.includes(day);
                return (
                  <TouchableOpacity
                    key={day}
                    style={[styles.dayChip, selected && styles.dayChipSelected]}
                    onPress={() => toggleTransportDay(day)}>
                    <Text
                      style={[
                        styles.dayChipText,
                        selected && styles.dayChipTextSelected,
                      ]}>
                      {day}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={[styles.dayCard, styles.dayCardSelected, { marginTop: 16 }]}>
              <View style={styles.dayCardHeader}>
                <Text style={styles.dayName}>Kids</Text>
                <Text style={styles.dayTotal}>Total: ${Number(planPrice).toFixed(2)}</Text>
              </View>
              <Text style={styles.selectKidsLabel}>
                Applied to all selected days
              </Text>
              {transportKids.map(kid => (
                <TouchableOpacity
                  key={kid.id}
                  style={styles.kidItem}
                  onPress={() =>
                    setTransportKids(transportKids.filter(k => k.id !== kid.id))
                  }>
                  <Image
                    source={
                      kid.profilePic
                        ? { uri: kid.profilePic }
                        : require('../../assets/AddKidPic.png')
                    }
                    style={styles.kidAvatar}
                  />
                  <View style={styles.kidInfo}>
                    <Text style={styles.kidName}>{kid.name}</Text>
                    <Text style={styles.kidDetails}>{kid.age} Years</Text>
                  </View>
                  <Image source={CheckIcon} style={styles.checkIcon} />
                </TouchableOpacity>
              ))}
              {transportKids.length < maxKidsPerDay && (
                <TouchableOpacity
                  style={styles.addSiblingButton}
                  onPress={() => setTransportModalOpen(true)}>
                  <Image source={PlusIcon} style={styles.plusIcon} />
                  <Text style={styles.addSiblingText}>
                    {transportKids.length === 0 ? 'Select kids' : 'Add sibling'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>
          <TouchableOpacity style={styles.nextButton} onPress={handleTransportConfirm}>
            <Text style={styles.nextButtonText}>Confirm Selection</Text>
          </TouchableOpacity>
        </View>

        <SelectKidsModal
          visible={transportModalOpen}
          title={'Select Kid(s)'}
          kids={allKidsData}
          initialSelectedIds={transportKids.map(kid => kid.id)}
          maxKids={maxKidsPerDay}
          pricePerMonth={planPrice}
          onConfirm={kids => {
            setTransportKids(kids);
            setTransportModalOpen(false);
          }}
          onClose={() => setTransportModalOpen(false)}
          onAddKid={handleAddKid}
        />
      </>
    );
  }

  if (isFixedWeekdays) {
    return (
      <>
        <TopGreenHeader
          Heading={'Select Kids'}
          navigation={navigation}
          isBackIcon={true}
          isNotification={false}
        />
        <View style={styles.container}>
          <ToastContainer />
          <Text style={styles.title}>
            This plan runs Monday to Friday. Select up to {maxKidsPerDay} kid(s) for your plan.
          </Text>
          <ScrollView showsVerticalScrollIndicator={false}>
            <View style={[styles.dayCard, styles.dayCardSelected]}>
              <View style={styles.dayCardHeader}>
                <Text style={styles.dayName}>Monday - Friday</Text>
                <Text style={styles.dayTotal}>Total: ${Number(planPrice).toFixed(2)}</Text>
              </View>
              <Text style={styles.selectKidsLabel}>Kids</Text>
              {fixedKids.map(kid => (
                <TouchableOpacity
                  key={kid.id}
                  style={styles.kidItem}
                  onPress={() => setFixedKids(fixedKids.filter(k => k.id !== kid.id))}>
                  <Image
                    source={
                      kid.profilePic
                        ? { uri: kid.profilePic }
                        : require('../../assets/AddKidPic.png')
                    }
                    style={styles.kidAvatar}
                  />
                  <View style={styles.kidInfo}>
                    <Text style={styles.kidName}>{kid.name}</Text>
                    <Text style={styles.kidDetails}>{kid.age} Years</Text>
                  </View>
                  <Image source={CheckIcon} style={styles.checkIcon} />
                </TouchableOpacity>
              ))}
              {fixedKids.length < maxKidsPerDay && (
                <TouchableOpacity style={styles.addSiblingButton} onPress={() => setFixedModalOpen(true)}>
                  <Image source={PlusIcon} style={styles.plusIcon} />
                  <Text style={styles.addSiblingText}>
                    {fixedKids.length === 0 ? 'Select kids' : 'Add sibling'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </ScrollView>
          <TouchableOpacity style={styles.nextButton} onPress={handleFixedConfirm}>
            <Text style={styles.nextButtonText}>Confirm Selection</Text>
          </TouchableOpacity>
        </View>

        <SelectKidsModal
          visible={fixedModalOpen}
          title={'Select Kid(s)'}
          kids={allKidsData}
          initialSelectedIds={fixedKids.map(kid => kid.id)}
          maxKids={maxKidsPerDay}
          pricePerMonth={planPrice}
          onConfirm={kids => {
            setFixedKids(kids);
            setFixedModalOpen(false);
          }}
          onClose={() => setFixedModalOpen(false)}
          onAddKid={handleAddKid}
        />
      </>
    );
  }

  return (
    <>
      <TopGreenHeader
        Heading={'Select Days'}
        navigation={navigation}
        isBackIcon={true}
        isNotification={false}
      />
      <View style={styles.container}>
        <ToastContainer style={{ zIndex: 999999 }} />
        <Text style={styles.title}>
          {isSocialHub
            ? 'Select days (Sunday - Saturday) and kids for each day (1-3 kids per day)'
            : `You can select upto ${maxDays} days and minimum ${minDays} day(s) - tap a day to select kids for it`
          }
        </Text>
        <ScrollView showsVerticalScrollIndicator={false}>
          {displayedDays.map(renderDayCard)}
        </ScrollView>
        {isSocialHub && selectedDays.length > 0 && (
          <View style={styles.grandTotalContainer}>
            <Text style={styles.grandTotalText}>
              Grand Total: ${socialHubGrandTotal.toFixed(2)}/month
            </Text>
          </View>
        )}
        <TouchableOpacity style={styles.nextButton} onPress={handleConfirmSelection}>
          <Text style={styles.nextButtonText}>Confirm Selection</Text>
        </TouchableOpacity>
      </View>

      <SelectKidsModal
        visible={modalDay !== null}
        title={modalDay ? `Select kids for ${modalDay}` : 'Select Kid(s)'}
        kids={allKidsData}
        initialSelectedIds={modalDay ? (kidsByDay[modalDay] || []).map(kid => kid.id) : []}
        maxKids={maxKidsPerDay}
        pricePerMonth={isSocialHub ? undefined : planPrice}
        onConfirm={handleModalConfirm}
        onClose={() => setModalDay(null)}
        onAddKid={handleAddKid}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: 'white',
  },
  title: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 10,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#666',
    marginBottom: 10,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  dayChipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  dayChip: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: '#ddd',
    backgroundColor: '#fff',
    marginRight: 8,
    marginBottom: 8,
  },
  dayChipSelected: {
    borderColor: '#A6CE39',
    backgroundColor: '#f0f9e0',
  },
  dayChipText: {
    fontSize: 14,
    color: '#666',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  dayChipTextSelected: {
    color: '#5a8a1a',
    fontWeight: 'bold',
  },
  dayCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 5,
    elevation: 2,
  },
  dayCardSelected: {
    borderWidth: 1.5,
    borderColor: '#A6CE39',
  },
  dayCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dayName: {
    fontSize: 18,
    color: 'black',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  dayTotal: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#8dc63f',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  selectKidsLabel: {
    fontSize: 14,
    color: '#666',
    marginTop: 10,
    marginBottom: 8,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  kidItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginBottom: 8,
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    justifyContent: 'space-between',
  },
  kidAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    marginRight: 12,
    backgroundColor: '#eee',
  },
  kidInfo: {
    flex: 1,
  },
  kidName: {
    fontSize: 16,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  kidDetails: {
    fontSize: 14,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    color: '#666',
  },
  checkIcon: {
    width: 20,
    height: 20,
    margin: 2,
  },
  addSiblingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#ccc',
    borderRadius: 8,
    paddingVertical: 12,
  },
  plusIcon: {
    width: 16,
    height: 16,
    marginRight: 8,
  },
  addSiblingText: {
    fontSize: 14,
    color: '#666',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  grandTotalContainer: {
    backgroundColor: '#f0f9e0',
    padding: 12,
    borderRadius: 8,
    marginTop: 10,
    alignItems: 'center',
  },
  grandTotalText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#8dc63f',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  nextButton: {
    backgroundColor: '#A3CD3B',
    padding: 15,
    alignItems: 'center',
    marginTop: 20,
    borderRadius: 5,
  },
  nextButtonText: {
    fontSize: 18,
    color: 'white',
    fontWeight: 'bold',
    fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
});

export default SelectDaysScreen;
